import React, { useState, useRef, useCallback, useContext } from "react";
import axios from "axios";
import JSZip from "jszip";
import { AuthContext } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const MAX_SIZE = 4 * 1024 * 1024 * 1024; // 4 GB

const MODES = [
  { id: "zip",    label: "ZIP Archive",   hint: ".zip with .mhd + .raw or DICOM series" },
  { id: "mhd",    label: "MHD + RAW",     hint: "Select .mhd and .raw file pair" },
  { id: "folder", label: "DICOM Folder",  hint: "Folder containing .dcm files" },
];

/* ── SVG icons ─────────────────────────────────────────────────────────────── */

function FileIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="12" y1="18" x2="12" y2="12"/>
      <line x1="9" y1="15" x2="15" y2="15"/>
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16,16 12,12 8,16"/>
      <line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

/* ── Component ─────────────────────────────────────────────────────────────── */

export default function UploadPanel({ onUploadComplete }) {
  const { token } = useContext(AuthContext);
  const { addNotification } = useNotifications();
  const [mode, setMode]               = useState("zip");
  const [dragOver, setDragOver]       = useState(false);
  const [files, setFiles]             = useState([]);     // File[]
  const [patientName, setPatientName] = useState("");
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [stage, setStage]             = useState("");      // "" | "zipping" | "uploading"
  const [error, setError]             = useState("");

  const zipInputRef    = useRef(null);
  const mhdInputRef    = useRef(null);
  const folderInputRef = useRef(null);

  /* ── Validation per mode ─────────────────────────────────────────────────── */
  const validate = useCallback((fileList, m) => {
    const arr = Array.from(fileList);
    if (arr.length === 0) return "No files selected.";
    const names   = arr.map((f) => f.name.toLowerCase());
    const totalSz = arr.reduce((s, f) => s + f.size, 0);
    if (totalSz > MAX_SIZE) return "Total size exceeds 4 GB limit.";

    switch (m) {
      case "zip":
        if (arr.length !== 1)            return "Select a single .zip file.";
        if (!names[0].endsWith(".zip"))  return "File must be a .zip archive.";
        break;
      case "mhd": {
        const mhd = names.filter((n) => n.endsWith(".mhd"));
        const raw = names.filter((n) => n.endsWith(".raw"));
        if (mhd.length === 0) return "Please select a .mhd file.";
        if (mhd.length > 1)   return "Select only one .mhd file.";
        if (raw.length === 0)  return "Please also select the companion .raw file.";
        if (raw.length > 1)    return "Select only one .raw file.";
        const extra = names.filter((n) => !n.endsWith(".mhd") && !n.endsWith(".raw"));
        if (extra.length > 0)  return `Unexpected file(s): ${extra.join(", ")}. Only .mhd and .raw allowed.`;
        break;
      }
      case "folder": {
        const dcm = arr.filter((f) => f.name.toLowerCase().endsWith(".dcm"));
        if (dcm.length < 2) return `Need at least 2 .dcm files for a 3D volume (found ${dcm.length}).`;
        break;
      }
      default:
        return "Unknown upload mode.";
    }
    return null;
  }, []);

  const handleSelect = useCallback(
    (fileList) => {
      let combined;
      if (mode === "mhd") {
        // In MHD mode, accumulate files so user can pick .mhd and .raw separately
        const incoming = Array.from(fileList);
        const merged = [...files];
        for (const f of incoming) {
          const ext = f.name.toLowerCase().split(".").pop();
          // Replace any existing file of the same extension
          const idx = merged.findIndex((m) => m.name.toLowerCase().endsWith(`.${ext}`));
          if (idx !== -1) merged[idx] = f;
          else merged.push(f);
        }
        combined = merged;
      } else {
        combined = Array.from(fileList);
      }

      const err = validate(combined, mode);
      if (err && !(mode === "mhd" && combined.length < 2)) {
        // In MHD mode, don't show error until user has had a chance to add the second file
        setError(err); setFiles(mode === "mhd" ? combined : []);
        return;
      }
      setFiles(combined);
      setError("");
    },
    [mode, files, validate],
  );

  /* ── Mode switch resets state ────────────────────────────────────────────── */
  const switchMode = (m) => {
    if (uploading) return;
    setMode(m);
    setFiles([]);
    setError("");
    setProgress(0);
    setStage("");
    [zipInputRef, mhdInputRef, folderInputRef].forEach((r) => { if (r.current) r.current.value = ""; });
  };

  /* ── Drag-and-drop (zip & mhd modes) ────────────────────────────────────── */
  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      if (mode === "folder") return;           // folder requires directory picker
      if (e.dataTransfer.files.length) handleSelect(e.dataTransfer.files);
    },
    [mode, handleSelect],
  );

  const onDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = ()  => setDragOver(false);

  /* ── Upload flow ─────────────────────────────────────────────────────────── */
  const handleUpload = async () => {
    if (!files.length) { setError("Please select files."); return; }

    // Re-validate before upload
    const vErr = validate(files, mode);
    if (vErr) { setError(vErr); return; }

    setUploading(true);
    setProgress(0);
    setError("");
    setStage("");

    try {
      let uploadBlob, uploadName;

      if (mode === "zip") {
        /* Direct .zip upload */
        uploadBlob = files[0];
        uploadName = files[0].name;
      } else {
        /* Client-side ZIP packaging (STORE — no compression, fast) */
        setStage("zipping");
        const zip = new JSZip();
        const toAdd =
          mode === "folder"
            ? files.filter((f) => f.name.toLowerCase().endsWith(".dcm"))
            : files;

        for (const f of toAdd) {
          zip.file(f.name, f);
        }

        uploadBlob = await zip.generateAsync(
          { type: "blob", compression: "STORE" },
          ({ percent }) => setProgress(Math.round(percent)),
        );

        uploadName =
          mode === "mhd"
            ? files.find((f) => f.name.toLowerCase().endsWith(".mhd")).name.replace(/\.mhd$/i, "_scan.zip")
            : "dicom_series.zip";
        setProgress(0);
      }

      /* Step 1 — presigned S3 URL */
      setStage("uploading");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(`${API_BASE}/api/upload/initiate`, {
        filename:     uploadName,
        patient_name: patientName.trim() || "Anonymous",
        file_size:    uploadBlob.size,
      }, { headers: authHeaders });

      /* Step 2 — upload to S3 */
      await axios.put(data.upload_url, uploadBlob, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: ({ total, loaded }) => {
          if (total) setProgress(Math.round((loaded / total) * 100));
        },
      });

      /* Step 3 — notify parent */
      addNotification({
        type: "analysis",
        title: "Scan Uploaded",
        message: `CT scan uploaded and queued for analysis.`,
      });
      onUploadComplete(data.job_id);
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || e.message || "Upload failed. Please try again.";
      setError(msg);
      setUploading(false);
      setProgress(0);
      setStage("");
    }
  };

  const reset = () => {
    setFiles([]);
    setPatientName("");
    setError("");
    setProgress(0);
    setStage("");
    setUploading(false);
    [zipInputRef, mhdInputRef, folderInputRef].forEach((r) => { if (r.current) r.current.value = ""; });
  };

  /* ── Helpers for render ──────────────────────────────────────────────────── */
  const hasFiles  = files.length > 0;
  const totalMB   = (files.reduce((s, f) => s + f.size, 0) / (1024 * 1024)).toFixed(1);

  const fileSummary = () => {
    if (!hasFiles) return null;
    if (mode === "zip")    return { name: files[0].name, detail: `${totalMB} MB` };
    if (mode === "mhd")    return { name: files.map((f) => f.name).join(" + "), detail: `${totalMB} MB` };
    const dcmCount = files.filter((f) => f.name.toLowerCase().endsWith(".dcm")).length;
    return { name: `${dcmCount} DICOM slices`, detail: `${totalMB} MB` };
  };

  const summary   = fileSummary();
  const stageText = stage === "zipping"
    ? `Packaging files… ${progress}%`
    : progress < 100
      ? `Uploading scan… ${progress}%`
      : "Queuing analysis…";

  /* ── Render ──────────────────────────────────────────────────────────────── */
  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      {/* Page title */}
      <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 8 }}>
          Lung CT Scan Analysis
        </h1>
        <p style={{ color: "var(--text-secondary)", lineHeight: 1.6 }}>
          Upload a 3D CT volume for AI-powered pulmonary nodule detection.<br />
          Supported: 
          1. You can upload a DICOM files entire Folder.
          2. Make a zipped file with MHD & RAW files.
          3. Or direct MHD, RAW files (make sure to upload perfect pair of files).
        </p>
      </div>

      <div className="card">
        <p className="card-title">Upload CT Scan</p>

        {/* ── Mode tabs ──────────────────────────────────────────────────── */}
        <div
          style={{
            display:      "flex",
            gap:          4,
            marginBottom: "1.25rem",
            background:   "var(--bg-input)",
            borderRadius: "var(--radius-sm)",
            padding:      3,
          }}
        >
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => switchMode(m.id)}
              disabled={uploading}
              style={{
                flex:         1,
                padding:      "7px 6px",
                fontSize:     "0.78rem",
                fontWeight:   mode === m.id ? 600 : 400,
                fontFamily:   "var(--font)",
                color:        mode === m.id ? "var(--text-primary)" : "var(--text-muted)",
                background:   mode === m.id ? "var(--bg-card)" : "transparent",
                border:       mode === m.id ? "1px solid var(--border)" : "1px solid transparent",
                borderRadius: "var(--radius-sm)",
                cursor:       uploading ? "default" : "pointer",
                transition:   "all 0.15s",
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* ── Drop zone ──────────────────────────────────────────────────── */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => {
            if (uploading) return;
            if (mode === "zip")    zipInputRef.current?.click();
            if (mode === "mhd")    mhdInputRef.current?.click();
            if (mode === "folder") folderInputRef.current?.click();
          }}
          style={{
            border:        `2px dashed ${dragOver ? "var(--accent-blue)" : hasFiles ? "var(--accent-green)" : "var(--border)"}`,
            borderRadius:  "var(--radius-md)",
            padding:       "2.5rem 1.5rem",
            textAlign:     "center",
            cursor:        uploading ? "default" : "pointer",
            transition:    "border-color 0.2s, background 0.2s",
            background:    dragOver
              ? "rgba(59,130,246,0.05)"
              : hasFiles
              ? "rgba(16,185,129,0.05)"
              : "transparent",
          }}
        >
          {/* Hidden file inputs */}
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            style={{ display: "none" }}
            onChange={(e) => e.target.files.length && handleSelect(e.target.files)}
          />
          <input
            ref={mhdInputRef}
            type="file"
            accept=".mhd,.raw"
            multiple
            style={{ display: "none" }}
            onChange={(e) => e.target.files.length && handleSelect(e.target.files)}
          />
          <input
            ref={folderInputRef}
            type="file"
            /* webkitdirectory enables folder selection in Chrome/Edge/Firefox */
            webkitdirectory=""
            directory=""
            style={{ display: "none" }}
            onChange={(e) => e.target.files.length && handleSelect(e.target.files)}
          />

          <div style={{ color: hasFiles ? "var(--accent-green)" : "var(--text-muted)", marginBottom: 12 }}>
            {mode === "folder" ? <FolderIcon /> : <FileIcon />}
          </div>

          {summary ? (
            <>
              <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: 4 }}>{summary.name}</p>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                {summary.detail} ·{" "}
                <span style={{ color: "var(--accent-green)" }}>Ready to upload</span>
              </p>
            </>
          ) : (
            <>
              <p style={{ fontWeight: 600, marginBottom: 6 }}>
                {mode === "zip"    && "Drag & drop your ZIP archive here"}
                {mode === "mhd"    && "Drag & drop .mhd and .raw files here"}
                {mode === "folder" && "Click to select a DICOM folder"}
              </p>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                {mode === "zip"    && "ZIP containing .mhd + .raw (LUNA16) or DICOM series (.dcm files)"}
                {mode === "mhd"    && "Select both the .mhd header and its companion .raw data file"}
                {mode === "folder" && "Folder must contain at least 2 .dcm files (DICOM 3D volume)"}
                {" · Max 4 GB"}
              </p>
            </>
          )}
        </div>

        {/* Patient name */}
        <div style={{ marginTop: "1.25rem" }}>
          <label style={{ display: "block", fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: 6 }}>
            Patient Name <span style={{ color: "var(--text-muted)" }}>(optional)</span>
          </label>
          <input
            type="text"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            disabled={uploading}
            placeholder="e.g. John Doe"
            style={{
              width:        "100%",
              padding:      "9px 14px",
              background:   "var(--bg-input)",
              border:       "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              color:        "var(--text-primary)",
              fontSize:     "0.875rem",
              fontFamily:   "var(--font)",
              outline:      "none",
              transition:   "border-color 0.15s",
            }}
            onFocus={(e)  => (e.target.style.borderColor = "var(--border-focus)")}
            onBlur={(e)   => (e.target.style.borderColor = "var(--border)")}
          />
        </div>

        {/* Upload progress */}
        {uploading && (
          <div style={{ marginTop: "1.25rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "0.82rem", color: "var(--text-secondary)" }}>
              <span>{stageText}</span>
              <span>{progress}%</span>
            </div>
            <div className="progress-bar-track">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            marginTop:    "1rem",
            padding:      "10px 14px",
            background:   "rgba(239,68,68,0.1)",
            border:       "1px solid rgba(239,68,68,0.3)",
            borderRadius: "var(--radius-sm)",
            color:        "var(--accent-red)",
            fontSize:     "0.84rem",
          }}>
            {error}
          </div>
        )}

        <hr className="divider" />

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={handleUpload}
            disabled={!hasFiles || uploading}
          >
            {uploading ? (
              <>
                <span className="spinner" style={{ width: 16, height: 16 }} />
                {stageText}
              </>
            ) : (
              <>
                <UploadIcon />
                Analyse Scan
              </>
            )}
          </button>
          {hasFiles && !uploading && (
            <button className="btn btn-secondary" onClick={reset}>
              Clear
            </button>
          )}
        </div>

        {/* Info chips */}
        <div style={{ display: "flex", gap: 8, marginTop: "1.25rem", flexWrap: "wrap" }}>
          {[
            "Lung segmentation (lungmask)",
            "U-Net nodule detection",
            "Annotated PNG export",
            "DICOM overlay export",
          ].map((item) => (
            <span
              key={item}
              style={{
                padding:      "3px 10px",
                background:   "rgba(59,130,246,0.08)",
                border:       "1px solid rgba(59,130,246,0.15)",
                borderRadius: 20,
                fontSize:     "0.7rem",
                color:        "var(--text-secondary)",
              }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
